/**
 * Event capacity helpers.
 *
 * `capacity === null` means the event has no guest limit. That matches
 * `events.capacity` in the database, which is nullable with NULL = unlimited
 * (migration 090), and mirrors how `vouchers.redemption_limit` already works.
 *
 * Every capacity read in the UI goes through these so "unlimited" is handled in
 * one place instead of being re-derived (and re-forgotten) per component.
 */

export type Capacity = number | null | undefined;

export function isUnlimitedCapacity(capacity: Capacity): boolean {
  return capacity == null;
}

/** Spots remaining for guests, or `null` when the event is unlimited. */
export function spotsRemaining(capacity: Capacity, participantCount: number): number | null {
  if (capacity == null) return null;
  return Math.max(0, capacity - participantCount);
}

/**
 * Whether the host is one of the people going. `events.host_attending`
 * (migration 107) defaults to true; a ticketed or business event turns it off
 * so the host does not use up a place. Anything not explicitly false counts.
 */
export type HostAttending = boolean | null | undefined;

/** 1 when the host takes a place, else 0. */
export function hostSeat(hostAttending: HostAttending): number {
  return hostAttending === false ? 0 : 1;
}

/** People going: approved guests, plus the host when they are taking part. */
export function goingCount(guests: number, hostAttending: HostAttending = true): number {
  return guests + hostSeat(hostAttending);
}

/** Total places as people see them (host included when attending), or `null` when unlimited. */
export function totalSpots(capacity: Capacity, hostAttending: HostAttending = true): number | null {
  return capacity == null ? null : capacity + hostSeat(hostAttending);
}

/**
 * The number a host types in the form ("4 people" / "100 tickets") to the guest
 * places the database stores. The triggers only ever count guests, so this is the
 * one place the host's seat is taken off.
 */
export function toStoredCapacity(places: number | null, hostAttending: HostAttending): number | null {
  return places == null ? null : places - hostSeat(hostAttending);
}

/** Inverse of toStoredCapacity, for pre-filling the form from a saved event. */
export function toPlaces(capacity: Capacity, hostAttending: HostAttending): number | null {
  return capacity == null ? null : capacity + hostSeat(hostAttending);
}

/** Fewest places a host can offer: with the host attending they need at least one guest. */
export function minPlaces(hostAttending: HostAttending): number {
  return 1 + hostSeat(hostAttending);
}

/**
 * Progress-bar fill as a 0–100 percentage, or `null` when there is no ceiling
 * to fill against — callers should hide the bar rather than draw it at 0 or 100.
 */
export function fillPercent(capacity: Capacity, participantCount: number): number | null {
  if (capacity == null || capacity <= 0) return null;
  return Math.min(100, (participantCount / capacity) * 100);
}

/** "3 spots left" / "Event full" / "Unlimited spots" */
export function formatSpotsLeft(capacity: Capacity, participantCount: number): string {
  const left = spotsRemaining(capacity, participantCount);
  if (left === null) return 'Unlimited spots';
  if (left <= 0) return 'Event full';
  return `${left} ${left === 1 ? 'spot' : 'spots'} left`;
}

/**
 * Head count — "5/11", or just "5" when the event is unlimited and there is no
 * denominator to show. The host is included only when they are attending.
 * Callers supply their own trailing label (e.g. `{formatAttendance(...)} going`).
 */
export function formatAttendance(
  capacity: Capacity,
  participantCount: number,
  hostAttending: HostAttending = true,
): string {
  const filled = goingCount(participantCount, hostAttending);
  const total = totalSpots(capacity, hostAttending);
  return total === null ? `${filled}` : `${filled}/${total}`;
}

export type UrgencyTone = 'calm' | 'warm' | 'hot' | 'full';

export interface Urgency {
  tone: UrgencyTone;
  /** Headline under the avatars, e.g. "Only 1 spot left. Don't miss out". */
  label: string;
}

/**
 * Conversion nudge for the "Who's going" block. Based on guest places only
 * (the host's seat is already out of `capacity`), and `null` when unlimited,
 * since an event with no ceiling can't be "filling up".
 */
export function urgencyFor(capacity: Capacity, guests: number): Urgency | null {
  const left = spotsRemaining(capacity, guests);
  if (left === null || capacity == null) return null;
  if (left <= 0) return { tone: 'full', label: 'Event full' };
  if (left === 1) return { tone: 'hot', label: "Only 1 spot left. Don't miss out" };
  const filled = capacity > 0 ? guests / capacity : 0;
  if (filled >= 0.75 || left <= 3) return { tone: 'hot', label: `Filling up fast. ${left} spots left` };
  if (filled >= 0.5) return { tone: 'warm', label: `Over half full. ${left} spots left` };
  return { tone: 'calm', label: `${left} spots left` };
}
